"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setIsLoading(true);

    try {
      const API_BASE = (import.meta.env.VITE_API_URL || "") + "/api";
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch {
      setSent(true); // tetap show success (jangan bocorkan info)
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg px-4 pt-20 pb-24">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-gold mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Kembali ke Login
        </Link>

        <div className={cn("rounded-[28px] p-8", "bg-light-card dark:bg-dark-card", "border border-dark-text-secondary/5 dark:border-white/5", "shadow-sm")}>
          {sent ? (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">Cek Email Anda</h2>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Jika email terdaftar, link reset password telah dikirim ke <strong>{email}</strong>.
                Link berlaku 1 jam.
              </p>
              <Link to="/login" className="inline-block mt-6 text-gold hover:text-gold-light font-medium transition-colors">
                Kembali ke Login
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">Lupa Password</h2>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-6">
                Masukkan email terdaftar, kami akan kirim link reset password.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary/50" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@email.com"
                    className={cn("w-full pl-11 pr-4 py-3 rounded-2xl", "bg-light-bg dark:bg-dark-bg", "border border-dark-text-secondary/10 dark:border-white/10", "text-light-text-primary dark:text-dark-text-primary", "placeholder:text-dark-text-secondary/40", "focus:outline-none focus:border-gold/40")}
                  />
                </div>
                <button type="submit" disabled={isLoading || !email}
                  className={cn("w-full py-3.5 rounded-2xl font-semibold", "transition-all duration-300", email ? "bg-gold text-black hover:bg-gold-light" : "bg-dark-text-secondary/10 text-dark-text-secondary/40 cursor-not-allowed")}>
                  {isLoading ? "Mengirim..." : "Kirim Link Reset"}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
